import { ExecutionStep, PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppsIcon from '@mui/icons-material/Apps';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LayersIcon from '@mui/icons-material/Layers';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import { Box, Chip, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { HistoryEntry, RightPanelContext } from './types';

type Props = {
  historyList?: HistoryEntry[];
  currentHistoryIndex?: number;
  rightPanelContext: RightPanelContext;
  pipelineExecutionState?: PipelineExecutionState;
  selectedStep?: ExecutionStep | null;
  onHistoryBack?: () => void;
  onHistoryForward?: () => void;
  onHistoryJump?: (index: number) => void;
  onResetContext?: () => void;
};

const getCategoryLabel = (type?: string): string => {
  switch (type) {
    case 'data_source':
    case 'dataSourceInstance':
    case 'dataSourceDef':
    case 'dataSourceItem':
    case 'dataSourceItemGroup':
      return 'Data Sources';
    case 'agent':
    case 'agentInstance':
    case 'agentDef':
      return 'Agents';
    case 'pipeline':
    case 'pipelineInstance':
    case 'pipelineBlueprint':
      return 'Pipelines';
    case 'user_app':
    case 'userAppInstance':
    case 'userAppDef':
      return 'User Apps';
    default:
      return 'Components';
  }
};

const getStatusColor = (status?: string): 'default' | 'success' | 'secondary' | 'error' | 'info' => {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (['completed', 'active', 'ready', 'success'].includes(s)) return 'success';
  if (['running', 'processing', 'pending', 'in_progress'].includes(s)) return 'secondary';
  if (['failed', 'error', 'inactive'].includes(s)) return 'error';
  return 'default';
};

const EntityContextHeader = ({
  historyList = [],
  currentHistoryIndex = 0,
  rightPanelContext,
  pipelineExecutionState,
  selectedStep,
  onHistoryBack,
  onHistoryForward,
  onHistoryJump,
  onResetContext,
}: Props) => {
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState<null | HTMLElement>(null);

  const headerIcon =
    rightPanelContext.type === 'entity_list' ? (
      <LayersIcon color="primary" sx={{ fontSize: 22 }} />
    ) : rightPanelContext.type === 'empty' ? (
      <AutoAwesomeIcon sx={{ fontSize: 22, color: '#f59e0b' }} />
    ) : rightPanelContext.entityType === 'dataSourceInstance' ? (
      <StorageIcon sx={{ fontSize: 22, color: '#0284c7' }} />
    ) : rightPanelContext.entityType === 'agentInstance' ? (
      <SmartToyIcon sx={{ fontSize: 22, color: '#8b5cf6' }} />
    ) : rightPanelContext.entityType === 'userAppInstance' ? (
      <AppsIcon sx={{ fontSize: 22, color: '#f59e0b' }} />
    ) : (
      <AccountTreeIcon color="primary" sx={{ fontSize: 22 }} />
    );

  const title =
    rightPanelContext.type === 'entity_list'
      ? rightPanelContext.entityList?.title || 'Component list'
      : rightPanelContext.type === 'empty'
        ? 'Workspace Suggestions & Quick Actions'
        : rightPanelContext.entityName || pipelineExecutionState?.pipelineName || 'Pipeline Execution';

  const isSingleEntity = rightPanelContext.type !== 'pipeline';
  const subtitle =
    rightPanelContext.type === 'entity_list'
      ? `${rightPanelContext.entityList?.items.length} item(s) found`
      : rightPanelContext.type === 'empty'
        ? 'Select a prompt to start exploring components or building pipelines'
        : isSingleEntity
          ? `${getCategoryLabel(rightPanelContext.type)} Instance • ${rightPanelContext.entityId || selectedStep?.entityId}`
          : `ID: ${pipelineExecutionState?.id}`;

  return (
    <Box sx={{ bgcolor: '#ffffff', px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {(currentHistoryIndex > 0 || currentHistoryIndex < historyList.length - 1 || historyList.length > 1) && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mr: 0.5 }}>
              <Tooltip title="Previous Context">
                <span>
                  <IconButton
                    size="small"
                    disabled={!(currentHistoryIndex > 0)}
                    onClick={onHistoryBack}
                    sx={{ p: 0.5, color: '#64748b' }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next Context">
                <span>
                  <IconButton
                    size="small"
                    disabled={!(currentHistoryIndex < historyList.length - 1)}
                    onClick={onHistoryForward}
                    sx={{ p: 0.5, color: '#64748b' }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {historyList.length > 1 && onHistoryJump && (
                <>
                  <Tooltip title="Context History">
                    <IconButton
                      size="small"
                      onClick={(e) => setHistoryMenuAnchor(e.currentTarget)}
                      sx={{ p: 0.5, color: '#64748b' }}
                    >
                      <HistoryIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={historyMenuAnchor}
                    open={Boolean(historyMenuAnchor)}
                    onClose={() => setHistoryMenuAnchor(null)}
                    PaperProps={{ sx: { maxHeight: 320, width: 280 } }}
                  >
                    <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}
                      >
                        Context History ({historyList.length})
                      </Typography>
                    </Box>
                    {historyList.map((entry, idx) => (
                      <MenuItem
                        key={entry.id || idx}
                        selected={idx === currentHistoryIndex}
                        onClick={() => {
                          onHistoryJump(idx);
                          setHistoryMenuAnchor(null);
                        }}
                        sx={{ py: 1, px: 2 }}
                      >
                        <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: idx === currentHistoryIndex ? 700 : 500, fontSize: '0.8rem' }}
                            >
                              {entry.title}
                            </Typography>
                            {idx === currentHistoryIndex && (
                              <Chip
                                label="Current"
                                size="small"
                                color="primary"
                                sx={{ height: 16, fontSize: '0.58rem' }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                            {getCategoryLabel(entry.type)} &bull;{' '}
                            {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, height: 18, my: 'auto' }} />
            </Stack>
          )}
          {headerIcon}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b', lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', fontSize: '0.72rem' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {rightPanelContext.type !== 'entity_list' && (
            <Stack direction="row" spacing={1} alignItems="center">
              {pipelineExecutionState?.status && (
                <Chip
                  label={pipelineExecutionState.status.toUpperCase()}
                  size="small"
                  color={getStatusColor(pipelineExecutionState.status)}
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
              {pipelineExecutionState?.steps && !isSingleEntity && (
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                  {pipelineExecutionState.steps.length} Step(s)
                </Typography>
              )}
            </Stack>
          )}
          {onResetContext && rightPanelContext.type !== 'empty' && (
            <Tooltip title="Close and return to Suggestions">
              <IconButton size="small" onClick={onResetContext} sx={{ color: '#94a3b8', p: 0.5 }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default EntityContextHeader;
